import { logger } from "@clockwise/client-commons/src/util/logger";
import {
  EventId,
  useReadActiveEvent,
  useUpdateActiveEvent,
} from "@clockwise/web-commons/src/util/ActiveEventContext";
import { encode } from "@clockwise/web-commons/src/util/eventKeyEncoding";
import { useRouter } from "found";
import { compose } from "lodash/fp";
import { useEffect } from "react";
import { useEffectOnce } from "usehooks-ts";
import { FOCUSED_EVENT_ID } from "../../constants";
import { getActiveEventKeyFromDefault } from "../util/getActiveEventKeyFromDefault";
import { getActiveEventKeyFromRoute } from "../util/getActiveEventKeyFromRoute";
import { getActiveEventKeyFromState } from "../util/getActiveEventKeyFromState";
import { getActiveEventKeyFromStorage } from "../util/getActiveEventKeyFromStorage";

const LogIfError = (message: string) => (result: Error | null | EventId) => {
  if (result instanceof Error) {
    logger.error(message, result);
    console.error(message);
    return null;
  }

  return result;
};

const loggedGetActiveEventKeyFromRoute = compose(
  LogIfError("Unable to get event from url param"),
  getActiveEventKeyFromRoute,
);

const loggedGetActiveEventKeyFromStorage = compose(
  LogIfError("Unable to get event from session storage"),
  getActiveEventKeyFromStorage,
);

export const usePersistActiveEvent = () => {
  const { router, match } = useRouter();

  const activeEvent = useReadActiveEvent();
  const updateActiveEvent = useUpdateActiveEvent();

  // Set initial active event
  useEffectOnce(() => {
    const activeEventKey =
      getActiveEventKeyFromState(activeEvent) ||
      loggedGetActiveEventKeyFromRoute(match) ||
      loggedGetActiveEventKeyFromStorage(sessionStorage) ||
      getActiveEventKeyFromDefault();

    updateActiveEvent(activeEventKey);
  });

  // On Active Event Change: Update Route
  useEffect(() => {
    if (activeEvent) {
      router.push(`/app/event/${encode(activeEvent)}${window.location.hash}`);
    } else {
      router.push(`/app/planner${window.location.hash}`);
    }
  }, [activeEvent, router]);

  // On Active Event Change: Update Storage
  useEffect(() => {
    if (activeEvent) {
      sessionStorage.setItem(FOCUSED_EVENT_ID, encode(activeEvent));
    } else {
      sessionStorage.removeItem(FOCUSED_EVENT_ID);
    }
  }, [activeEvent]);
};
